import { useQuery } from '@tanstack/react-query';
import { Form, Input, Select, Skeleton } from 'antd';
import { useEffect } from 'react';

import { DepositCollectionPointValues, DepositRefundPointValues } from '@/api/bookings';

import { apiFetch } from '@/api/client';
import { submitVia, useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { handleEnterKeyNavigation, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { dateFormats, userDateFormat } from '@core/format/datePreference';
import { paperLabel, paperSizes } from '@core/print/sheetHtml';
import { humanizeEnum } from '@core/format/text';
import { canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { accentOptions, defaultAccent, userAccentChoice } from '@/shell/accent';
import { defaultTextScale, textScaleOptions, userTextScale } from '@/shell/textScale';

type ConfigValueType = 'String' | 'Number' | 'Boolean' | 'Json' | 'Secret';

interface TenantConfigSetting {
  name: string;
  /** Always null for a Secret, set or not — the server never sends one back. */
  value: string | null;
  valueType: ConfigValueType;
  isSet: boolean;
  manageByUser: boolean;
  description: string | null;
}

const settingsKey = ['tenant-config'] as const;

const AWS = {
  accessKeyId: 'Storage.Aws.AccessKeyId',
  secretAccessKey: 'Storage.Aws.SecretAccessKey',
  bucketName: 'Storage.Aws.BucketName',
  region: 'Storage.Aws.Region',
  serviceUrl: 'Storage.Aws.ServiceUrl',
} as const;

/** The four that must all be present before uploads go anywhere but the platform's own bucket. */
const REQUIRED = [AWS.bucketName, AWS.region, AWS.accessKeyId, AWS.secretAccessKey] as const;

/**
 * How the product reads across every screen — the date pattern, and how large it draws itself.
 *
 * <p>
 * On this card rather than in the account menu because these are the company's house settings: a
 * shop agrees one way of writing a date and everyone reads it, which is the point. An individual can
 * still override either for their own device from the account menu, where they need no permission —
 * these are behind `Tenant:Edit`, since they change what every colleague sees.
 * </p>
 *
 * <p>
 * <strong>Text size is not cosmetic and its default is deliberately not the old appearance.</strong>
 * The product was scaled for density — 13px type on 34px controls — which is right for an analyst's
 * screen and wrong for a counter, where the person is standing and the control has to be hit as
 * well as read. Leaving it unset gives the larger scale; `compact` is how a shop asks for the old
 * one back.
 * </p>
 */
const DISPLAY = {
  dateFormat: 'Display.DateFormat',
  textScale: 'Display.TextScale',
  accent: 'Display.Accent',
  printPaper: 'Print.DefaultPaper',
} as const;

/**
 * The shop's own commercial defaults.
 *
 * <p>
 * <strong>Every one of these was declared in `TenantConfigKeys` and unreachable.</strong> The
 * catalogue has carried them since the config module landed, all company-managed, and this screen
 * held only the storage keys — so `RentalOrderItem.AlterationBillable` documented "Defaults from a
 * tenant setting; the counter can override it" against a setting nobody could set. A shop that
 * alters free had to untick the box on every garment of every booking, for ever.
 * </p>
 */
const BOOKING = {
  alterationBillable: 'Booking.Alteration.Billable',
  issueRequiresPhoto: 'Booking.Issue.RequirePhoto',
  depositMultiplier: 'Booking.Deposit.Multiplier',
  depositCollectionPoint: 'Booking.Deposit.CollectionPoint',
  depositRefundPoint: 'Booking.Deposit.RefundPoint',
  collectBeforeDays: 'Booking.Dates.CollectBeforeDays',
  returnAfterDays: 'Booking.Dates.ReturnAfterDays',
  lateFeeGraceDays: 'Booking.LateFee.GraceDays',
  lateFeePerDay: 'Booking.LateFee.PerDay',
  // Declared with the approval control and left unreachable, which is the same gap the six above
  // were added to close. Without an editor the threshold is always zero, so Section 9's approval queue
  // can never hold anything and the control exists only in the enum.
  damageApprovalThreshold: 'Booking.Damage.ApprovalThreshold',
} as const;

/**
 * <strong>Blank means "use the built-in default", not "leave unchanged"</strong> — the rule for both
 * forms below, and the opposite of the storage card, where the difference is the secrets. Nothing
 * here is write-only, so every field can show what is actually in force, which means an empty one
 * can honestly mean "nothing is set" and clearing it can delete the row. The storage card cannot
 * offer that, because a blank secret field is indistinguishable from a secret the server will not
 * send back.
 */
/** Whole days, 0 to 14, or blank — what `BookingDefaults.MaxGraceDays` accepts. */
const graceRule = {
  pattern: /^\s*(\d|1[0-4])?\s*$/,
  message: 'Whole days, from 0 to 14.',
};

interface DisplayFormValues {
  dateFormat?: string | undefined;
  textScale?: string | undefined;
  accent?: string | undefined;
  printPaper?: string | undefined;
}

interface BookingFormValues {
  alterationBillable?: string | undefined;
  issueRequiresPhoto?: string | undefined;
  depositMultiplier?: string | undefined;
  depositCollectionPoint?: string | undefined;
  depositRefundPoint?: string | undefined;
  collectBeforeDays?: string | undefined;
  returnAfterDays?: string | undefined;
  lateFeeGraceDays?: string | undefined;
  lateFeePerDay?: string | undefined;
  damageApprovalThreshold?: string | undefined;
}

// `| undefined` spelled out on each: the project runs `exactOptionalPropertyTypes`, and the two
// secrets are deliberately reset to undefined on every load rather than merely left absent.
interface StorageFormValues {
  bucketName?: string | undefined;
  region?: string | undefined;
  serviceUrl?: string | undefined;
  accessKeyId?: string | undefined;
  secretAccessKey?: string | undefined;
}

/** What the built-in default renders, so the placeholder and the hint cannot drift apart. */
const defaultSample = dateFormats[0]?.sample ?? '31/12/2026';

/** As above, and read off the option list so renaming a step reaches the placeholder too. */
const defaultScaleLabel =
  textScaleOptions.find((option) => option.value === defaultTextScale)?.label ?? 'Comfortable';

/**
 * "a", "a and b", "a, b and c".
 *
 * <p>
 * A helper rather than `join(' and ')`, which was fine while there were two of these and reads as
 * a child wrote it at three. There will be a fourth the next time a display setting is added.
 * </p>
 */
function listOf(items: readonly string[]): string {
  if (items.length <= 1) {
    return items[0] ?? '';
  }

  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const defaultAccentLabel =
  accentOptions.find((option) => option.value === defaultAccent)?.label ?? 'Peacock';

/**
 * Company settings — today, where this shop's uploaded files go.
 *
 * Three things about this screen follow from the shape of the data rather than from taste.
 *
 * A **secret is write-only**: the API returns `value: null` for one whether or not it is set, so
 * there is nothing to prefill and the field starts empty with the *state* shown beside it instead.
 * Rendering a fake mask in the box would invite someone to submit the mask back as the new value.
 *
 * An **empty secret field means "leave it alone"**, not "clear it". Anything else would force a
 * shop correcting its bucket name to retype both credentials, and would silently wipe them the one
 * time somebody saved without noticing.
 *
 * And the settings are **all-or-nothing**, because the resolver treats them that way: a bucket with
 * no credentials falls back to the platform's storage rather than writing into a named bucket with
 * borrowed keys. So the screen states which of the two is in force rather than leaving it to be
 * inferred from four fields.
 */
export function SettingsPage({ session }: { session: Session }) {
  const [form] = Form.useForm<StorageFormValues>();
  const [bookingForm] = Form.useForm<BookingFormValues>();
  const [displayForm] = Form.useForm<DisplayFormValues>();

  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: () => apiFetch<TenantConfigSetting[]>('/api/tenant-config'),
  });

  const editable = canAct(session, 'Tenant:Edit');
  // Gated on `editable` for the reason the company profile is: a read-only view has every field
  // disabled, so there is nothing to land on.
  const fields = useAutoFocusFirstField<HTMLDivElement>(editable);

  const byName = new Map((settings.data ?? []).map((s) => [s.name, s]));
  const valueOf = (name: string) => byName.get(name)?.value ?? undefined;
  const isSet = (name: string) => byName.get(name)?.isSet ?? false;

  const usingOwnBucket = REQUIRED.every(isSet);

  // Only the plain values are prefilled; the two secrets deliberately start empty every time.
  useEffect(() => {
    if (settings.data) {
      form.setFieldsValue({
        bucketName: valueOf(AWS.bucketName),
        region: valueOf(AWS.region),
        serviceUrl: valueOf(AWS.serviceUrl),
        accessKeyId: undefined,
        secretAccessKey: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data]);

  useEffect(() => {
    if (settings.data) {
      displayForm.setFieldsValue({
        dateFormat: valueOf(DISPLAY.dateFormat),
        textScale: valueOf(DISPLAY.textScale),
        accent: valueOf(DISPLAY.accent),
        printPaper: valueOf(DISPLAY.printPaper),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data]);

  // Prefilled from what is actually stored, so a blank field honestly reads as "nothing is set".
  useEffect(() => {
    if (settings.data) {
      bookingForm.setFieldsValue({
        alterationBillable: valueOf(BOOKING.alterationBillable),
        issueRequiresPhoto: valueOf(BOOKING.issueRequiresPhoto),
        depositMultiplier: valueOf(BOOKING.depositMultiplier),
        depositCollectionPoint: valueOf(BOOKING.depositCollectionPoint),
        depositRefundPoint: valueOf(BOOKING.depositRefundPoint),
        collectBeforeDays: valueOf(BOOKING.collectBeforeDays),
        returnAfterDays: valueOf(BOOKING.returnAfterDays),
        lateFeeGraceDays: valueOf(BOOKING.lateFeeGraceDays),
        lateFeePerDay: valueOf(BOOKING.lateFeePerDay),
        damageApprovalThreshold: valueOf(BOOKING.damageApprovalThreshold),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data]);

  /*
    Blank means "use the built-in default", as everywhere else on this screen bar the storage card —
    so clearing DELETEs the row rather than writing an empty string, which the reader would then hand
    to dayjs as a format and get an empty date out of.

    Nothing writes to `datePreference` here. The company's format reaches the store through the
    session, and `invalidates` below refetches this list but not `/api/auth/me` — so the change shows
    on this card at once and on every other screen at the next session read. Writing it locally as
    well would put a second source of the answer on the one screen that must not have one: a shop
    would see its own change applied while nobody else's screen had it.
  */
  const saveDisplay = useApiMutation<void, DisplayFormValues>({
    mutationFn: async (values) => {
      const writes: [string, string | undefined][] = [
        [DISPLAY.dateFormat, values.dateFormat],
        [DISPLAY.textScale, values.textScale],
        [DISPLAY.accent, values.accent],
        [DISPLAY.printPaper, values.printPaper],
      ];

      for (const [name, raw] of writes) {
        const value = raw?.trim();

        // Cleared means "fall back to the built-in default", so the row is deleted rather than
        // written as an empty string — which the reader would parse as a value and refuse.
        if (!value) {
          if (isSet(name)) {
            await apiFetch(`/api/tenant-config/${encodeURIComponent(name)}`, { method: 'DELETE' });
          }

          continue;
        }

        await apiFetch('/api/tenant-config', { method: 'PUT', body: { name, value } });
      }
    },
    success: 'Display settings saved. Everyone signing in sees them.',
    invalidates: [settingsKey],
  });

  const saveBooking = useApiMutation<void, BookingFormValues>({
    mutationFn: async (values) => {
      const writes: [string, string | undefined][] = [
        [BOOKING.alterationBillable, values.alterationBillable],
        [BOOKING.issueRequiresPhoto, values.issueRequiresPhoto],
        [BOOKING.depositMultiplier, values.depositMultiplier],
        [BOOKING.depositCollectionPoint, values.depositCollectionPoint],
        [BOOKING.depositRefundPoint, values.depositRefundPoint],
        [BOOKING.collectBeforeDays, values.collectBeforeDays],
        [BOOKING.returnAfterDays, values.returnAfterDays],
        [BOOKING.lateFeeGraceDays, values.lateFeeGraceDays],
        [BOOKING.lateFeePerDay, values.lateFeePerDay],
        [BOOKING.damageApprovalThreshold, values.damageApprovalThreshold],
      ];

      for (const [name, raw] of writes) {
        const value = raw?.trim();

        // Cleared means "fall back to the built-in default", so the row is deleted rather than
        // written as an empty string — which the reader would then parse as a value and fail on.
        if (!value) {
          if (isSet(name)) {
            await apiFetch(`/api/tenant-config/${encodeURIComponent(name)}`, { method: 'DELETE' });
          }

          continue;
        }

        await apiFetch('/api/tenant-config', { method: 'PUT', body: { name, value } });
      }
    },
    success: 'Booking defaults saved.',
    invalidates: [settingsKey],
  });

  const save = useApiMutation<void, StorageFormValues>({
    mutationFn: async (values) => {
      // One PUT per changed setting: the endpoint takes a single name and value, which keeps the
      // catalogue lookup and the ManageByUser check in one place rather than looping server-side.
      const writes: [string, string | undefined][] = [
        [AWS.bucketName, values.bucketName],
        [AWS.region, values.region],
        [AWS.serviceUrl, values.serviceUrl],
        [AWS.accessKeyId, values.accessKeyId],
        [AWS.secretAccessKey, values.secretAccessKey],
      ];

      for (const [name, raw] of writes) {
        const value = raw?.trim();

        // Blank means "unchanged" — see the note on this component. Clearing is what the
        // "Use platform storage" button is for, since a half-cleared set is not a state the
        // resolver has an answer for.
        if (!value) {
          continue;
        }

        await apiFetch('/api/tenant-config', { method: 'PUT', body: { name, value } });
      }
    },
    success: 'Storage settings saved.',
    invalidates: [settingsKey],
  });

  const revert = useApiMutation<void, void>({
    mutationFn: async () => {
      for (const name of Object.values(AWS)) {
        if (!isSet(name)) {
          continue;
        }

        await apiFetch(`/api/tenant-config/${encodeURIComponent(name)}`, { method: 'DELETE' });
      }
    },
    success: 'Now using the platform’s storage.',
    invalidates: [settingsKey],
  });

  if (settings.isError) {
    return <ApiErrorAlert error={settings.error} />;
  }

  return (
    <>
      <PageHeading
        title="Settings"
        subtitle="How the app reads, what a new booking starts with, and where this company’s uploaded files are stored."
      />

      {settings.isLoading || !settings.data ? (
        <Card>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      ) : (
        <>
          <Card className="mb-4">
            <h2 className="text-sm font-bold text-gray-900">Display</h2>
            <p className="text-default mt-1 mb-3 text-[13px]">
              How dates are written and how large the app draws itself, on every screen. Leave
              either blank for the built-in setting. Anyone can override both for their own device
              from the account menu.
            </p>

            <Form<DisplayFormValues>
              form={displayForm}
              layout="vertical"
              disabled={!editable}
              requiredMark={false}
              onFinish={(values) => void submitVia(saveDisplay, values)}
              onKeyDown={handleEnterKeyNavigation}
            >
              <div className="grid gap-x-4 md:grid-cols-2">
                <Form.Item
                  name="dateFormat"
                  label="Date format"
                  extra="Printed customer copies and dockets always spell the month out, whatever is chosen here — that slip leaves the shop, and 11/12 is the ambiguity it cannot afford."
                >
                  <Select
                    allowClear
                    placeholder={`Built-in default — ${defaultSample}`}
                    options={dateFormats.map((format) => ({
                      value: format.value,
                      label: `${format.sample}  ·  ${format.value}`,
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  name="textScale"
                  label="Text size"
                  extra="Comfortable is the built-in size. Compact is what the app looked like before this setting existed — more rows on screen, and controls below the 44px a counter tablet is built around."
                >
                  <Select
                    allowClear
                    placeholder={`Built-in default — ${defaultScaleLabel}`}
                    options={textScaleOptions.map((option) => ({
                      value: option.value,
                      label: `${option.label}  ·  ${option.hint}`,
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  name="accent"
                  label="Theme"
                  extra="The shop’s colour on the website and the phone app: links, buttons and highlights here, and the band at the top of each screen on the phone. Each person can pick their own from their account menu. Red still means late and amber still means needs attention, whichever theme is chosen."
                >
                  <Select
                    allowClear
                    placeholder={`Built-in default — ${defaultAccentLabel}`}
                    options={accentOptions.map((option) => ({
                      value: option.value,
                      label: `${option.label}  ·  ${option.hint}`,
                    }))}
                  />
                </Form.Item>

                {/* Where every print screen starts, on the website and the phone (2026-10-15). A
                    clerk can still change it for one print; a roll printer's shop sets a roll. */}
                <Form.Item
                  name="printPaper"
                  label="Paper documents start on"
                  extra="Receipts, booking summaries and invoices open on this paper when printed or sent. Anyone can switch for a single print. The website prints rolls at 80 mm."
                >
                  <Select
                    allowClear
                    placeholder="Built-in default — A5"
                    options={paperSizes.map((value) => ({ value, label: paperLabel[value] }))}
                  />
                </Form.Item>
              </div>

              {/* Said out loud, because it is otherwise invisible: this card shows the *company's*
                  setting, and somebody who has overridden it would change this, see nothing move,
                  and reasonably conclude it had not saved. */}
              {(userDateFormat() !== null ||
                userTextScale() !== null ||
                userAccentChoice() !== null) && (
                <div className="mb-4">
                  <Notice tone="info" title="You are not seeing all of this yourself">
                    Your account menu has{' '}
                    {listOf(
                      [
                        userDateFormat() !== null ? 'a date format' : null,
                        userTextScale() !== null ? 'a text size' : null,
                        userAccentChoice() !== null ? 'a brand colour' : null,
                      ].filter((item): item is string => item !== null),
                    )}{' '}
                    set for this device, which wins over the company&rsquo;s. Clear it there to
                    follow whatever is chosen here.
                  </Notice>
                </div>
              )}

              {editable && (
                <Button variant="primary" type="submit" disabled={saveDisplay.isPending}>
                  Save display settings
                </Button>
              )}
            </Form>
          </Card>

          <Card className="mb-4">
            <h2 className="text-sm font-bold text-gray-900">Booking defaults</h2>
            <p className="text-default mt-1 mb-3 text-[13px]">
              What a new booking starts with. Every one of these is overridable at the counter —
              these are the shop’s habits, not its rules. Leave a field blank to use the built-in
              default.
            </p>

            <Form<BookingFormValues>
              form={bookingForm}
              layout="vertical"
              disabled={!editable}
              requiredMark={false}
              onFinish={(values) => void submitVia(saveBooking, values)}
              onKeyDown={handleEnterKeyNavigation}
            >
              <div className="grid gap-x-4 md:grid-cols-2">
                {/* The one that prompted this card. Many shops in this trade alter free and
                    advertise it — and until now had to untick the box on every garment of every
                    booking, because the setting this defaults from had no editor. The *cost* is
                    recorded either way; this only decides whether it reaches the invoice. */}
                <Form.Item
                  name="alterationBillable"
                  label="Alteration charges"
                  extra="Absorbed work is still costed against the garment — free to the customer is never free to the shop."
                >
                  <Select
                    allowClear
                    placeholder="Built-in default — billed"
                    options={[
                      { value: 'true', label: 'Billed to the customer' },
                      { value: 'false', label: 'Absorbed by the shop' },
                    ]}
                  />
                </Form.Item>

                {/* Some shops photograph every garment going out and some never do (owner,
                    2026-10-17). Off, the hand-over shows no camera and asks nothing. */}
                <Form.Item
                  name="issueRequiresPhoto"
                  label="Photograph at hand-over"
                  extra="Asked for, the counter photographs each garment going out or says it is going without one."
                >
                  <Select
                    allowClear
                    placeholder="Built-in default — not asked for"
                    options={[
                      { value: 'true', label: 'Asked for' },
                      { value: 'false', label: 'Not asked for' },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  name="depositMultiplier"
                  label="Deposit multiplier"
                  extra="A multiple of the rental, e.g. 1.5. Built-in default is 1.5."
                >
                  <Input
                    type="number"
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    step="0.1"
                    placeholder="1.5"
                  />
                </Form.Item>
              </div>

              <div className="grid gap-x-4 md:grid-cols-2">
                <Form.Item name="depositCollectionPoint" label="Deposit taken">
                  <Select
                    allowClear
                    placeholder="Built-in default — at booking"
                    options={DepositCollectionPointValues.map((value) => ({
                      value,
                      label: humanizeEnum(value),
                    }))}
                  />
                </Form.Item>

                {/* AtCounter is the fast option and has a stated cost: closing the deposit before
                    cleaning has seen the garments makes anything found afterwards unsecured. */}
                <Form.Item
                  name="depositRefundPoint"
                  label="Deposit returned"
                  extra="Returning at the counter is fastest, and leaves anything found later unsecured."
                >
                  <Select
                    allowClear
                    placeholder="Built-in default — after inspection"
                    options={DepositRefundPointValues.map((value) => ({
                      value,
                      label: humanizeEnum(value),
                    }))}
                  />
                </Form.Item>
              </div>

              {/* Free days either side of the event: they move collect and return, and block the
                  calendar, but never the charge — shops price the event, not the day. Whole days
                  up to a fortnight, which is what `BookingDefaults.cs` reads; anything else it
                  would quietly read as 0, so the form refuses it instead. */}
              <div className="grid gap-x-4 md:grid-cols-2">
                <Form.Item
                  name="collectBeforeDays"
                  label="Collected before the event (days)"
                  extra="Blank or 0: collected on the first event day."
                  rules={[graceRule]}
                >
                  <Input
                    type="number"
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    max={14}
                    step="1"
                    placeholder="0"
                  />
                </Form.Item>

                <Form.Item
                  name="returnAfterDays"
                  label="Returned after the event (days)"
                  extra="Blank or 0: back on the last event day. Not charged either way."
                  rules={[graceRule]}
                >
                  <Input
                    type="number"
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    max={14}
                    step="1"
                    placeholder="0"
                  />
                </Form.Item>
              </div>

              <div className="grid gap-x-4 md:grid-cols-2">
                <Form.Item
                  name="lateFeeGraceDays"
                  label="Late return grace (days)"
                  extra="Days forgiven before a fee starts."
                >
                  <Input
                    type="number"
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    step="1"
                    placeholder="0"
                  />
                </Form.Item>

                <Form.Item name="lateFeePerDay" label="Late fee per day">
                  <Input
                    type="number"
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    step="1"
                    placeholder="0"
                  />
                </Form.Item>
              </div>

              <div className="grid gap-x-4 md:grid-cols-2">
                {/* Blank and 0 mean the same thing here and it is not "free": they mean no
                    threshold, so every damage charge bills itself however large. A shop that has
                    not asked for the control must not discover it by having a settlement quietly
                    stop working, which is why the default is off rather than some safe-looking
                    figure. */}
                <Form.Item
                  name="damageApprovalThreshold"
                  label="Damage charge needing approval"
                  extra="Charges above this are recorded and held off the booking until a manager signs. Blank or 0 means none, so every charge bills itself."
                >
                  <Input
                    type="number"
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    step="100"
                    placeholder="none"
                  />
                </Form.Item>
              </div>

              {editable && (
                <Button variant="primary" type="submit" disabled={saveBooking.isPending}>
                  Save booking defaults
                </Button>
              )}
            </Form>
          </Card>

          <Card className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold text-gray-900">Storage in use</h2>
                <p className="mt-1 text-[13px] text-default">
                  {usingOwnBucket
                    ? 'Uploads go to this company’s own Amazon S3 bucket.'
                    : 'Uploads go to the storage configured on the server. Fill in all four fields below to use your own bucket instead.'}
                </p>
              </div>

              <StatusBadge tone={usingOwnBucket ? 'positive' : 'neutral'}>
                {usingOwnBucket ? 'Own bucket' : 'Platform storage'}
              </StatusBadge>
            </div>
          </Card>

          <Card>
            <Form<StorageFormValues>
              form={form}
              layout="vertical"
              disabled={!editable}
              requiredMark={false}
              onFinish={(values) => void submitVia(save, values)}
              onKeyDown={handleEnterKeyNavigation}
            >
              <div ref={fields} className="grid gap-x-4 md:grid-cols-2">
                <Form.Item
                  name="bucketName"
                  label="Bucket name"
                  extra="The S3 bucket uploaded files go to. Keep it private — files are served through short-lived signed links."
                >
                  <Input placeholder="sharma-suits-media" />
                </Form.Item>

                <Form.Item name="region" label="Region" extra="For example ap-south-1 (Mumbai).">
                  <Input placeholder="ap-south-1" className="font-mono-nums" />
                </Form.Item>
              </div>

              <div className="grid gap-x-4 md:grid-cols-2">
                <Form.Item
                  name="accessKeyId"
                  label="Access key ID"
                  extra={
                    isSet(AWS.accessKeyId)
                      ? 'Already set. Leave blank to keep it, or type a new one to replace it.'
                      : 'The IAM user’s access key ID.'
                  }
                >
                  <Input
                    autoComplete="off"
                    placeholder={isSet(AWS.accessKeyId) ? '••••••••' : ''}
                  />
                </Form.Item>

                <Form.Item
                  name="secretAccessKey"
                  label="Secret access key"
                  extra={
                    isSet(AWS.secretAccessKey)
                      ? 'Already set. Leave blank to keep it, or type a new one to replace it.'
                      : 'Stored encrypted. It is never shown again once saved.'
                  }
                >
                  <Input.Password
                    autoComplete="new-password"
                    placeholder={isSet(AWS.secretAccessKey) ? '••••••••' : ''}
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="serviceUrl"
                label="Endpoint (optional)"
                extra="Only for storage that speaks S3 without being AWS — MinIO, Wasabi, Cloudflare R2. Leave empty for AWS."
              >
                <Input placeholder="https://s3.example.com" />
              </Form.Item>

              <div className="mb-4">
                <Notice tone="warning" title="What these permissions allow">
                  The key you enter is used by this server to write your uploads and to sign the
                  links that show them. Give it access to this one bucket and nothing else — it
                  needs only to read, write and delete objects.
                </Notice>
              </div>

              {editable && (
                <div className="flex flex-wrap justify-between gap-3 border-t border-border-color pt-4">
                  <Button
                    variant="secondary"
                    type="button"
                    disabled={!usingOwnBucket || revert.isPending}
                    onClick={() => void submitVia(revert, undefined)}
                  >
                    {revert.isPending ? 'Removing…' : 'Use platform storage instead'}
                  </Button>

                  <Button variant="primary" type="submit" disabled={save.isPending}>
                    {save.isPending ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              )}
            </Form>
          </Card>
        </>
      )}
    </>
  );
}
